'use client'
import { cn } from '@/utilities/ui'
import useClickableCard from '@/utilities/useClickableCard'
import Link from 'next/link'
import React from 'react'

import type { Blog } from '@/payload-types'

import { Media } from '@/components/Media'

export type CardPostData = Pick<Blog, 'slug' | 'categories' | 'title'> & {
  seo?: Blog['seo']
  featuredImage?: Blog['featuredImage']
  excerpt?: Blog['excerpt']
}

export const Card: React.FC<{
  alignItems?: 'center'
  className?: string
  doc?: CardPostData
  relationTo?: 'blog'
  showCategories?: boolean
  title?: string
}> = (props) => {
  const { cardRef, linkRef } = useClickableCard({})
  const { className, doc, relationTo, title: titleFromProps } = props

  const { slug, title, featuredImage, seo } = doc || {}
  const metaImage = seo?.meta_image ?? featuredImage
  const titleToUse = titleFromProps || title
  const href = relationTo === 'blog' ? `/blogs/${slug}` : `/${relationTo}/${slug}`

  return (
    <article
      className={cn(
        'relative hover:cursor-pointer',
        className,
      )}
      ref={cardRef}
    >
      {/* Stretch link — makes entire card clickable */}
      <Link href={href} className="absolute inset-0 z-[1]" aria-label={titleToUse} tabIndex={-1} />
      {/* Image fills the card — overflow-hidden only on image container */}
      <div className="relative w-full overflow-hidden bg-neutral-100" style={{ height: '400px' }}>
        {!metaImage && (
          <div className="flex h-full items-center justify-center text-neutral-400 text-sm">
            No image
          </div>
        )}
        {metaImage && typeof metaImage !== 'string' && (
          <Media
            resource={metaImage}
            fill
            className="absolute inset-0"
            imgClassName="object-cover"
          />
        )}
      </div>

      {/* Title below image — outside overflow-hidden so shadow shows */}
      {titleToUse && (
        <div
          className="absolute bottom-0 left-0 right-0 p-[10px]"
          style={{
            background: 'rgba(255,255,255,0.88)',
            borderBottom: '5px solid #009bab',
            boxShadow: 'rgba(0, 0, 0, 0.5) 0px 0px 10px 0px',
          }}
        >
          <Link
            className="block text-[20px] font-semibold leading-[20px] text-[#1d1d1b] hover:text-[#009bab] transition-colors no-underline text-center"
            href={href}
            ref={linkRef}
          >
            {titleToUse}
          </Link>
        </div>
      )}
    </article>
  )
}
